/**
 * AI helper functions for the SPA.
 *
 * All inference happens on the LexGat backend (FastAPI on Fly.io / wherever
 * VITE_API_BASE_URL points). The Gemini key is no longer shipped to the
 * browser; the backend performs direct Gemini calls and returns HTML plus
 * optional `Source`s (library statute hits when applicable).
 *
 * The function signatures here intentionally match the pre-backend versions
 * so existing callers in `LexDraftEditor.tsx` and `CaseDetail.tsx` keep
 * working without changes.
 */

import { api, ApiNotConfiguredError, ApiError } from './apiClient';
import { captureSentryException } from './sentry';
import { resolvePartyLabelsForCase } from './caseTaxonomyRegistry';
import type { DraftTabContextPayload } from './draftTabAiContext';
import type { TemplateReferencePayload } from './templateAiDrafting';
import type { AiUsageReport } from './aiUsageTypes';
import type { WorkspaceContextPayload } from './workspaceContext';

export type { DraftTabContextPayload, AiUsageReport };
export type { WorkspaceContextPayload } from './workspaceContext';

export interface Source {
  sourceType?: 'library' | 'web';
  statuteId: string;
  sectionId: string | null;
  label: string;
  title: string | null;
  snippet: string | null;
  statuteTitle?: string | null;
  url?: string | null;
}

export interface DraftResult {
  html: string;
  sources: Source[];
  usage?: AiUsageReport;
}

export interface CaseAnswerResult {
  answerHtml: string;
  sources: Source[];
  usage?: AiUsageReport;
}

/** Structured case metadata returned by `/api/cases/extract-from-text`. */
export interface CaseExtractParty {
  name: string;
  role: string;
}

export interface CaseExtractResult {
  title: string;
  caseNumber: string;
  courtType: string;
  district: string;
  parties: CaseExtractParty[];
  caseType: string;
  nextHearingDate: string;
  nextAction: string;
}

/**
 * Per-request slot the chat panel reads after `askCaseQuestion` resolves so it
 * can render `<Sources>` chips next to the latest assistant message. We don't
 * bake this into the function return type because legacy callers expect a
 * plain string — see `LexDraftEditor.tsx` and `CaseDetail.tsx`.
 */
export const lastAiSources = {
  drafting: [] as Source[],
  caseQa: [] as Source[],
};

/** Latest backend usage report per feature (set on each completed AI call). */
export const lastAiUsage = {
  drafting: null as AiUsageReport | null,
  caseQa: null as AiUsageReport | null,
};

interface CaseContextPayload {
  workspaceId?: string;
  caseId?: string;
  title?: string;
  caseNumber?: string;
  matterType?: string;
  caseType?: string;
  category?: string;
  caseCategory?: string;
  courtType?: string;
  district?: string;
  judgeName?: string;
  status?: string;
  stage?: string;
  representingSide?: string;
  plaintiffs?: { name: string; role?: string }[];
  defendants?: { name: string; role?: string }[];
  nextHearingDate?: string;
  reliefsClaimed?: string[];
  primaryRelief?: string;
  lawyerName?: string;
  court?: string;
  documentType?: string;
  party1Label?: string;
  party2Label?: string;
  parentCaseId?: string;
  rootCaseId?: string;
  linkType?: string;
  linkedFromStage?: string;
  matterStatus?: string;
  criminalCategory?: string;
  proceedingType?: string;
  organizationId?: string;
  organizationName?: string;
}

/** Firestore case docs often store dates as Timestamp objects — API expects strings. */
function firestoreValueToApiString(val: unknown): string | undefined {
  if (val == null || val === '') return undefined;
  if (typeof val === 'string') return val;
  if (typeof val === 'number' && !Number.isNaN(val)) {
    return new Date(val).toISOString();
  }
  if (val && typeof val === 'object') {
    if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
      return (val as { toDate: () => Date }).toDate().toISOString();
    }
    if (typeof (val as { toMillis?: () => number }).toMillis === 'function') {
      return new Date((val as { toMillis: () => number }).toMillis()).toISOString();
    }
    if ('seconds' in (val as object)) {
      const sec = Number((val as { seconds: number }).seconds);
      if (!Number.isNaN(sec)) return new Date(sec * 1000).toISOString();
    }
  }
  return undefined;
}

function normalizePartiesForApi(val: unknown): { name: string; role?: string }[] {
  if (!Array.isArray(val)) return [];
  const out: { name: string; role?: string }[] = [];
  for (const p of val) {
    if (typeof p === 'string' && p.trim()) {
      out.push({ name: p.trim() });
      continue;
    }
    if (p && typeof p === 'object') {
      const name = String((p as { name?: unknown }).name ?? '').trim();
      if (!name) continue;
      const role = (p as { role?: unknown }).role;
      out.push({
        name,
        ...(typeof role === 'string' && role.trim() ? { role: role.trim() } : {}),
      });
    }
  }
  return out;
}

function normalizeReliefsForApi(val: unknown): string[] | undefined {
  if (val == null) return undefined;
  if (Array.isArray(val)) {
    const items = val.map((r) => String(r).trim()).filter(Boolean);
    return items.length ? items : undefined;
  }
  if (typeof val === 'string' && val.trim()) return [val.trim()];
  return undefined;
}

function buildCaseContext(caseData: any): CaseContextPayload | undefined {
  if (!caseData) return undefined;
  const partyLabels = resolvePartyLabelsForCase(caseData);
  const ctx: CaseContextPayload = {
    workspaceId: caseData?.workspaceId ?? caseData?.workspace_id,
    caseId: caseData?.id || caseData?.caseId,
    title: caseData?.title != null ? String(caseData.title) : undefined,
    caseNumber: caseData?.caseNumber != null ? String(caseData.caseNumber) : undefined,
    matterType: caseData?.matterType != null ? String(caseData.matterType) : undefined,
    caseType:
      caseData?.caseType != null
        ? String(caseData.caseType)
        : caseData?.caseMatter != null
          ? String(caseData.caseMatter)
          : undefined,
    category: caseData?.category != null ? String(caseData.category) : undefined,
    caseCategory:
      caseData?.caseCategory != null ? String(caseData.caseCategory) : undefined,
    courtType: caseData?.courtType != null ? String(caseData.courtType) : undefined,
    district: caseData?.district != null ? String(caseData.district) : undefined,
    judgeName: caseData?.judgeName != null ? String(caseData.judgeName) : undefined,
    status: caseData?.status != null ? String(caseData.status) : undefined,
    stage: caseData?.stage != null ? String(caseData.stage) : undefined,
    representingSide:
      caseData?.representingSide != null ? String(caseData.representingSide) : undefined,
    plaintiffs: normalizePartiesForApi(caseData?.plaintiffs),
    defendants: normalizePartiesForApi(caseData?.defendants),
    nextHearingDate: firestoreValueToApiString(caseData?.nextHearingDate),
    reliefsClaimed: normalizeReliefsForApi(caseData?.reliefsClaimed),
    primaryRelief:
      caseData?.primaryRelief != null ? String(caseData.primaryRelief) : undefined,
    lawyerName: caseData?.lawyerName != null ? String(caseData.lawyerName) : undefined,
    court: caseData?.court != null ? String(caseData.court) : undefined,
    documentType:
      caseData?.documentType != null ? String(caseData.documentType) : undefined,
    party1Label:
      caseData?.party1Label != null
        ? String(caseData.party1Label)
        : partyLabels.party1Label,
    party2Label:
      caseData?.party2Label != null
        ? String(caseData.party2Label)
        : partyLabels.party2Label,
    parentCaseId:
      caseData?.parentCaseId != null ? String(caseData.parentCaseId) : undefined,
    rootCaseId: caseData?.rootCaseId != null ? String(caseData.rootCaseId) : undefined,
    linkType: caseData?.linkType != null ? String(caseData.linkType) : undefined,
    linkedFromStage:
      caseData?.linkedFromStage != null ? String(caseData.linkedFromStage) : undefined,
    matterStatus:
      caseData?.matterStatus != null ? String(caseData.matterStatus) : undefined,
    criminalCategory:
      caseData?.criminalCategory != null ? String(caseData.criminalCategory) : undefined,
    proceedingType:
      caseData?.proceedingType != null ? String(caseData.proceedingType) : undefined,
    organizationId:
      caseData?.organizationId != null ? String(caseData.organizationId) : undefined,
    organizationName:
      caseData?.organizationName != null ? String(caseData.organizationName) : undefined,
  };
  if (ctx.plaintiffs?.length === 0) delete ctx.plaintiffs;
  if (ctx.defendants?.length === 0) delete ctx.defendants;
  return ctx;
}

export interface DraftingStreamOptions {
  templateMode?: boolean;
  templateTitle?: string;
  templateReference?: TemplateReferencePayload;
}

export interface DraftingTabContextOptions {
  activeTabId?: string;
  draftTabs?: DraftTabContextPayload[];
}

export interface DraftParagraphTargetOptions {
  targetParagraphId?: string;
  targetParagraphHtml?: string;
  lexCounselMode?: 'draft' | 'ask' | 'research' | 'case';
  documentScope?: boolean;
}

function buildDraftRequestBody(
  draftText: string,
  caseData: any,
  proceedings: any[],
  instruction: string,
  stream: boolean,
  templateOptions?: DraftingStreamOptions,
  tabContext?: DraftingTabContextOptions,
  counselChatMode?: boolean,
  workspaceContext?: WorkspaceContextPayload,
  paragraphTarget?: DraftParagraphTargetOptions,
) {
  return {
    instruction,
    draftText: draftText || '',
    caseContext: buildCaseContext(caseData),
    proceedings: Array.isArray(proceedings) ? proceedings : [],
    stream,
    ...(counselChatMode ? { counselChatMode: true } : {}),
    ...(tabContext?.activeTabId ? { activeTabId: tabContext.activeTabId } : {}),
    ...(tabContext?.draftTabs?.length ? { draftTabs: tabContext.draftTabs } : {}),
    ...(templateOptions?.templateMode
      ? {
          templateMode: true,
          templateTitle: templateOptions.templateTitle,
          templateReference: templateOptions.templateReference?.tabs,
        }
      : {}),
    ...(workspaceContext ? { workspaceContext } : {}),
    ...(paragraphTarget?.targetParagraphId
      ? { targetParagraphId: paragraphTarget.targetParagraphId }
      : {}),
    ...(paragraphTarget?.targetParagraphHtml
      ? { targetParagraphHtml: paragraphTarget.targetParagraphHtml }
      : {}),
    ...(paragraphTarget?.lexCounselMode
      ? { lexCounselMode: paragraphTarget.lexCounselMode }
      : {}),
    ...(paragraphTarget?.documentScope ? { documentScope: true } : {}),
  };
}

function fallbackHtml(): string {
  return '<p>Sorry, the AI assistant is not configured. Set <code>VITE_API_BASE_URL</code> in <code>.env</code> and restart the dev server.</p>';
}

/**
 * Drafting assistant — calls the backend LangGraph drafting agent.
 *
 * Signature unchanged from the previous direct-Gemini implementation so
 * existing call sites continue to work. The returned string is the same
 * sanitized HTML body the agent produced; sources are stashed in
 * `lastAiSources.drafting` for any UI that wants to render attribution.
 */
export async function askDraftingAssistant(
  draftText: string,
  caseData: any,
  proceedings: any[],
  instruction: string,
  tabContext?: DraftingTabContextOptions,
  counselChatMode?: boolean,
  workspaceContext?: WorkspaceContextPayload,
  paragraphTarget?: DraftParagraphTargetOptions,
): Promise<string> {
  try {
    const result = await api.post<DraftResult>(
      '/api/draft',
      buildDraftRequestBody(
        draftText,
        caseData,
        proceedings,
        instruction,
        false,
        undefined,
        tabContext,
        counselChatMode,
        workspaceContext,
        paragraphTarget,
      ),
    );
    lastAiSources.drafting = result.sources || [];
    lastAiUsage.drafting = result.usage ?? null;
    return result.html || '<p>Sorry, the assistant returned no content.</p>';
  } catch (err) {
    if (err instanceof ApiNotConfiguredError) return fallbackHtml();
    if (err instanceof ApiError) {
      console.error('askDraftingAssistant failed', err.status, err.body);
      captureSentryException(err, {
        fn: 'askDraftingAssistant',
        status: err.status,
        body: err.body,
      });
      throw err;
    }
    captureSentryException(err, { fn: 'askDraftingAssistant' });
    throw err;
  }
}

/**
 * Streaming variant of the drafting assistant. Yields SSE events as the graph
 * runs: `token` (LLM chunks during draft), `delta` (full HTML from post-draft
 * nodes), and `final` (post-processed sanitized HTML). Prefer `final` over
 * accumulated tokens when both are present.
 */
export async function* askDraftingAssistantStream(
  draftText: string,
  caseData: any,
  proceedings: any[],
  instruction: string,
  signal?: AbortSignal,
  templateOptions?: DraftingStreamOptions,
  tabContext?: DraftingTabContextOptions,
  counselChatMode?: boolean,
  workspaceContext?: WorkspaceContextPayload,
  paragraphTarget?: DraftParagraphTargetOptions,
): AsyncGenerator<{ type: string; data: any }> {
  if (!api.isConfigured()) {
    yield { type: 'final', data: { html: fallbackHtml(), sources: [] } };
    return;
  }
  for await (const event of api.stream<any>(
    '/api/draft/stream',
    buildDraftRequestBody(
      draftText,
      caseData,
      proceedings,
      instruction,
      true,
      templateOptions,
      tabContext,
      counselChatMode,
      workspaceContext,
      paragraphTarget,
    ),
    { signal },
  )) {
    if (event.event === 'final') {
      if (event.data?.sources) lastAiSources.drafting = event.data.sources;
      if (event.data?.usage) lastAiUsage.drafting = event.data.usage;
    }
    yield { type: event.event, data: event.data };
  }
}

/**
 * Extract case title, parties, court, etc. from plain document text (DOCX/draft).
 * Runs on the backend so no Gemini key is required in the browser.
 */
export async function extractCaseMetadataFromPlainText(
  plainText: string,
  signal?: AbortSignal,
): Promise<CaseExtractResult> {
  const trimmed = plainText.trim();
  if (!trimmed) {
    throw new Error('The document appears to be empty or contains no readable text.');
  }
  try {
    return await api.post<CaseExtractResult>(
      '/api/cases/extract-from-text',
      { plainText: trimmed },
      { signal },
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    if (err instanceof ApiNotConfiguredError) {
      throw new Error(
        'The AI assistant is not configured. Set VITE_API_BASE_URL in .env and restart the dev server.',
      );
    }
    if (err instanceof ApiError) {
      console.error('extractCaseMetadataFromPlainText failed', err.status, err.body);
      captureSentryException(err, {
        fn: 'extractCaseMetadataFromPlainText',
        status: err.status,
        body: err.body,
      });
      throw new Error(err.message || 'Failed to extract case metadata.');
    }
    captureSentryException(err, { fn: 'extractCaseMetadataFromPlainText' });
    throw err;
  }
}

/**
 * Streaming case Q&A — yields SSE events (`token`, `final`, `node`).
 */
export async function* askCaseQuestionStream(
  caseData: any,
  proceedings: any[],
  question: string,
  history: { role: string; text: string }[],
  signal?: AbortSignal,
  workspaceContext?: WorkspaceContextPayload,
): AsyncGenerator<{ type: string; data: any }> {
  if (!caseData) {
    yield {
      type: 'final',
      data: {
        answerHtml: 'Sorry, no case context is available to answer questions about.',
        sources: [],
      },
    };
    return;
  }
  if (!api.isConfigured()) {
    yield {
      type: 'final',
      data: {
        answerHtml:
          'The AI assistant is not configured. Ask your administrator to set VITE_API_BASE_URL.',
        sources: [],
      },
    };
    return;
  }
  for await (const event of api.stream<any>(
    '/api/case/ask/stream',
    {
      question,
      caseContext: buildCaseContext(caseData),
      proceedings: Array.isArray(proceedings) ? proceedings : [],
      history: history?.map((h) => ({ role: h.role, text: h.text })) ?? [],
      ...(workspaceContext ? { workspaceContext } : {}),
    },
    { signal },
  )) {
    if (event.event === 'final') {
      if (event.data?.sources) lastAiSources.caseQa = event.data.sources;
      if (event.data?.usage) lastAiUsage.caseQa = event.data.usage;
    }
    yield { type: event.event, data: event.data };
  }
}

export async function* askDashboardQuestionStream(
  workspaceId: string,
  question: string,
  dashboardBrief: Record<string, unknown>,
  signal?: AbortSignal,
): AsyncGenerator<{ type: string; data: any }> {
  if (!api.isConfigured()) {
    yield {
      type: 'final',
      data: {
        answerHtml:
          'The AI assistant is not configured. Ask your administrator to set VITE_API_BASE_URL.',
      },
    };
    return;
  }
  for await (const event of api.stream<any>(
    '/api/workspace/ask/stream',
    {
      question,
      workspaceId,
      dashboardBrief,
    },
    { signal },
  )) {
    if (event.event === 'final' && event.data?.usage) {
      lastAiUsage.caseQa = event.data.usage;
    }
    yield { type: event.event, data: event.data };
  }
}

/**
 * Case Q&A — non-streaming fallback (`POST /api/case/ask`).
 *
 * Signature unchanged. Sources for the latest answer are stashed in
 * `lastAiSources.caseQa` for the chat UI to render alongside the assistant
 * message.
 */
export async function askCaseQuestion(
  caseData: any,
  proceedings: any[],
  question: string,
  history: { role: string; text: string }[],
): Promise<string> {
  if (!caseData) {
    return 'Sorry, no case context is available to answer questions about.';
  }
  try {
    const result = await api.post<CaseAnswerResult>('/api/case/ask', {
      question,
      caseContext: buildCaseContext(caseData),
      proceedings: Array.isArray(proceedings) ? proceedings : [],
      history: history?.map((h) => ({ role: h.role, text: h.text })) ?? [],
    });
    lastAiSources.caseQa = result.sources || [];
    lastAiUsage.caseQa = result.usage ?? null;
    return result.answerHtml || 'Sorry, the assistant returned no content.';
  } catch (err) {
    if (err instanceof ApiNotConfiguredError) {
      return 'The AI assistant is not configured. Ask your administrator to set VITE_API_BASE_URL.';
    }
    if (err instanceof ApiError) {
      console.error('askCaseQuestion failed', err.status, err.body);
      captureSentryException(err, {
        fn: 'askCaseQuestion',
        status: err.status,
        body: err.body,
      });
      throw err;
    }
    captureSentryException(err, { fn: 'askCaseQuestion' });
    throw err;
  }
}

/**
 * Generate 3 short AI editing action suggestions for selected text via the backend.
 */
export async function getSelectionPrompts(
  selectedText: string,
  documentTitle: string,
): Promise<string[]> {
  try {
    const result = await api.post<{ prompts: string[] }>('/api/draft/selection-prompts', {
      selectedText: selectedText || '',
      documentTitle: documentTitle || '',
    });
    return result.prompts || [];
  } catch (err) {
    if (err instanceof ApiNotConfiguredError) return [];
    console.error('getSelectionPrompts failed', err);
    captureSentryException(err, { fn: 'getSelectionPrompts' });
    return ['Fix grammar and spelling', 'Make it more formal', 'Summarize this section'];
  }
}

export interface DraftingPromptsOptions extends DraftingTabContextOptions {
  documentTitle: string;
  draftContent: string;
  caseData: any;
  workspaceContext?: WorkspaceContextPayload;
}

export interface DraftingPromptsResult {
  prompts: string[];
  draftSummary?: string | null;
}

/**
 * Generate 3 drafting prompt suggestions for the active document tab via the backend.
 */
export interface WorkflowContextOptions extends DraftingTabContextOptions {
  documentTitle: string;
  draftContent: string;
  caseData: any;
  documentType?: string;
  selectedText?: string;
}

function isWorkflowNetworkError(err: unknown): boolean {
  if (err instanceof ApiNotConfiguredError) return true;
  if (err instanceof TypeError && /fetch|network/i.test(err.message)) return true;
  return false;
}

function workflowExcerptFromOptions(options: WorkflowContextOptions): string {
  const tab = options.draftTabs?.find((t) => t.isActive);
  if (tab?.contentExcerpt?.trim()) return tab.contentExcerpt.trim();
  const html = options.draftContent || '';
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length <= 12000 ? text : `${text.slice(0, 12000)}…`;
}

export type WorkflowSuggestionsResult = {
  suggestions: {
    id: string;
    label: string;
    type: string;
    category: string;
    command: string;
  }[];
  groups?: {
    category: string;
    label: string;
    suggestions: {
      id: string;
      label: string;
      type: string;
      category: string;
      command: string;
    }[];
  }[];
};

export async function getWorkflowSuggestions(
  options: WorkflowContextOptions,
): Promise<WorkflowSuggestionsResult> {
  const {
    documentTitle,
    draftContent,
    caseData,
    activeTabId,
    draftTabs,
    documentType,
  } = options;

  const localFallback = async (): Promise<WorkflowSuggestionsResult> => {
    const mod = await import('./draftingWorkflow/localSuggestions');
    if (!mod.hasLinkedCaseForSuggestions(caseData)) {
      return mod.getLocalGroupedWorkflowSuggestions();
    }
    return {
      suggestions: mod.getLocalWorkflowSuggestions(
        caseData,
        workflowExcerptFromOptions(options),
      ),
    };
  };

  if (!api.isConfigured()) {
    return localFallback();
  }

  const groupByCaseType = !(caseData?.id || caseData?.caseId);

  try {
    const result = await api.post<WorkflowSuggestionsResult>(
      '/api/draft/workflow/suggestions',
      {
        documentTitle: documentTitle || '',
        draftContent: draftContent || '',
        caseContext: buildCaseContext(caseData),
        groupByCaseType,
        ...(activeTabId ? { activeTabId } : {}),
        ...(draftTabs?.length ? { draftTabs } : {}),
        ...(documentType ? { documentType } : {}),
      },
    );
    const remote = result.suggestions || [];
    if (remote.length > 0) return result;
    return localFallback();
  } catch (err) {
    if (isWorkflowNetworkError(err)) {
      return localFallback();
    }
    console.warn('getWorkflowSuggestions failed, using local catalog', err);
    captureSentryException(err, { fn: 'getWorkflowSuggestions' });
    return localFallback();
  }
}

export async function resolveWorkflowCommand(
  options: WorkflowContextOptions & { commandId?: string; query?: string },
): Promise<{
  instruction: string;
  insertMode: string;
  commandId: string;
  label: string;
}> {
  const {
    documentTitle,
    draftContent,
    caseData,
    activeTabId,
    draftTabs,
    documentType,
    selectedText,
    commandId,
    query,
  } = options;

  const localResolve = async () => {
    const { resolveWorkflowCommandLocal } = await import('./draftingWorkflow/localSuggestions');
    return resolveWorkflowCommandLocal({
      commandId,
      query,
      caseData,
      draftContent,
      selectedText,
    });
  };

  if (!api.isConfigured()) {
    return localResolve();
  }

  try {
    return await api.post('/api/draft/workflow/resolve', {
      documentTitle: documentTitle || '',
      draftContent: draftContent || '',
      caseContext: buildCaseContext(caseData),
      ...(activeTabId ? { activeTabId } : {}),
      ...(draftTabs?.length ? { draftTabs } : {}),
      ...(documentType ? { documentType } : {}),
      selectedText: selectedText || '',
      ...(commandId ? { commandId } : {}),
      ...(query ? { query } : {}),
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return localResolve();
    }
    if (isWorkflowNetworkError(err)) {
      return localResolve();
    }
    throw err;
  }
}

export async function getDraftingPrompts(
  options: DraftingPromptsOptions,
): Promise<DraftingPromptsResult> {
  const { documentTitle, draftContent, caseData, activeTabId, draftTabs, workspaceContext } =
    options;
  try {
    const result = await api.post<DraftingPromptsResult>('/api/draft/prompts', {
      documentTitle: documentTitle || '',
      draftContent: draftContent || '',
      caseContext: buildCaseContext(caseData),
      ...(activeTabId ? { activeTabId } : {}),
      ...(draftTabs?.length ? { draftTabs } : {}),
      ...(workspaceContext ? { workspaceContext } : {}),
    });
    return {
      prompts: result.prompts || [],
      draftSummary: result.draftSummary ?? null,
    };
  } catch (err) {
    if (err instanceof ApiNotConfiguredError) return { prompts: [] };
    console.error('getDraftingPrompts failed', err);
    captureSentryException(err, { fn: 'getDraftingPrompts' });
    return { prompts: [] };
  }
}

/**
 * Legacy stub: some bundles still `import { ai }` from the old browser Gemini
 * client. Exporting this avoids a hard SyntaxError; callers must migrate to
 * `extractCaseMetadataFromPlainText` / `askDraftingAssistant` / `askCaseQuestion`.
 */
export const ai = {
  models: {
    generateContent: async (): Promise<never> => {
      throw new Error(
        'Browser Gemini was removed. Use extractCaseMetadataFromPlainText, askDraftingAssistant, or askCaseQuestion from src/lib/ai.ts.',
      );
    },
  },
} as const;
